import "@khmyznikov/pwa-install";
import {
  ActionIcon,
  AppShell,
  Avatar,
  Burger,
  Button,
  Group,
  Menu,
  Tooltip,
} from "@mantine/core";
import {
  IconDeviceDesktopDown,
  IconLogout,
  IconMenu2,
  IconMenuDeep,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  useAppShellLayout,
  useLogoutStore,
  useRestaurant,
} from "../../stores/stores";
import { PwaInstallHandle } from "../PwaInstall";
import BranchSelector from "./BranchSelector";

interface HeaderProps {
  opened: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

export function Header({ opened, onToggle, isMobile }: HeaderProps) {
  const { logout } = useLogoutStore();
  const { userName } = useRestaurant();
  const [userInitials, setUserInitials] = useState<string>("AU");
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useAppShellLayout();

  var pwa = document.getElementsByTagName(
    "pwa-install",
  )[0] as unknown as PwaInstallHandle;
  // @ts-ignore
  pwa ? (pwa.externalPromptEvent = window.promptEvent) : null;

  useEffect(() => {
    const getUserName = async () => {
      try {
        if (userName) {
          // Generate initials from name
          const nameParts = userName.trim().split(" ");
          let initials = "";

          if (nameParts.length === 1) {
            // Single name - take first letter
            initials = nameParts[0].charAt(0).toUpperCase();
          } else {
            // Multiple names - take first letter of first and last name
            initials =
              nameParts[0].charAt(0).toUpperCase() +
              nameParts[nameParts.length - 1].charAt(0).toUpperCase();
          }

          setUserInitials(initials);
        }
      } catch (error) {
        console.error("Error fetching user name:", error);
      }
    };
    getUserName();
  }, []);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <AppShell.Header
      style={{ backgroundColor: "#1A1A1A" }}
      className="border-b border-[#1A1A1A]"
      withBorder={false}
    >
      <Group
        h="100%"
        px={isMobile ? "xs" : "md"}
        gap={0}
        justify="space-between"
      >
        {/* Left side - Logo and Name */}
        <Group gap={"md"}>
          {isMobile && (
            <Burger
              opened={opened}
              onClick={onToggle}
              size="sm"
              color="white"
              aria-label="Toggle navigation"
            />
          )}
          {!isMobile && (
            <div className="flex items-center gap-3">
              <Tooltip
                label={
                  isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                }
                position="bottom"
              >
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  color="white"
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  aria-label={
                    isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                  }
                >
                  {isSidebarCollapsed ? (
                    <IconMenuDeep size={20} />
                  ) : (
                    <IconMenu2 size={20} />
                  )}
                </ActionIcon>
              </Tooltip>
              <img
                src="/icons/light-logo-2.png"
                style={{ height: "32px", width: "auto" }}
              />
            </div>
          )}
        </Group>

        {/* Middle - Branch Selector */}
        <div className="flex-1 flex justify-center">
          <BranchSelector isMobile={isMobile} />
        </div>

        {/* Right side - Notifications and Profile */}
        <Group gap="xs">
          {pwa && pwa.isInstallAvailable && (
            <Tooltip label="Install Application" position="bottom">
              <ActionIcon
                variant="light"
                color="#fff"
                size="lg"
                radius={"md"}
                onClick={() => pwa.showDialog(true)}
              >
                <IconDeviceDesktopDown size={18} />
              </ActionIcon>
            </Tooltip>
          )}
          <Menu shadow="md" width={200} zIndex={99999}>
            <Menu.Target>
              {isMobile ? (
                <ActionIcon variant="subtle" size="lg" color="white">
                  <Avatar variant="filled" size={32} radius="md" color="green">
                    {userInitials}
                  </Avatar>
                </ActionIcon>
              ) : (
                <Button
                  variant="subtle"
                  color="white"
                  leftSection={
                    <Avatar
                      variant="filled"
                      size={32}
                      radius="md"
                      color="green"
                    >
                      {userInitials}
                    </Avatar>
                  }
                >
                  {userName}
                </Button>
              )}
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconLogout size={16} />}
                onClick={handleLogout}
                color="red"
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </AppShell.Header>
  );
}
